import { useState, useEffect, useCallback, useRef } from 'react'
import {Search, ChevronRight, ChevronDown, Save, RotateCcw, Plus, Trash2, Eye, EyeOff, ExternalLink, AlertTriangle, Upload, Image as ImageIcon, X, FilePlus, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import { SEOPage, SiloType } from '@/data/seoPages'
import { uploadOneFile } from '@/lib/upload'
import { useSeoPages, getMergedSeoPagesSync } from '@/hooks/useSeoPages'
import { useAuth } from '@/hooks/useAuth'
import RichTextEditor from '@/components/RichTextEditor'

// ─── Compat: getter synchrone exposé pour l'ancien import ──────────────────
// Rends une lecture synchrone de la liste fusionnée (cache + statiques).
export function getMergedSeoPages(): SEOPage[] {
  return getMergedSeoPagesSync()
}

// ─── Slugify utility ────────────────────────────────────────────────────────
function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// ─── Types ───────────────────────────────────────────────────────────────────
type Section = { title: string; content: string }

interface EditState {
  title: string
  metaDescription: string
  h1: string
  intro: string
  keyword: string
  sections: Section[]
  heroImage: string
  isActive: boolean
}

function pageToEditState(page: SEOPage): EditState {
  return {
    title: page.title,
    metaDescription: page.metaDescription,
    h1: page.h1,
    intro: page.intro,
    keyword: page.keyword,
    sections: page.sections.map((s) => ({ ...s })),
    heroImage: page.heroImage || '',
    isActive: page.isActive !== false,
  }
}

// ─── Helper ──────────────────────────────────────────────────────────────────
function charCount(str: string) {
  return str.length
}

function MetaHint({ value, min, max }: { value: string; min: number; max: number }) {
  const len = charCount(value)
  const ok = len >= min && len <= max
  const warn = len > 0 && (len < min || len > max)
  return (
    <span
      className={`text-[11px] ml-1 font-medium ${ok ? 'text-emerald-600' : warn ? 'text-amber-600' : 'text-gray-400'}`}
    >
      {len}/{max} car.{ok ? ' ✓' : len > 0 ? ` (idéal : ${min}-${max})` : ''}
    </span>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function SeoEditor() {
  const { user } = useAuth()
  const {
    loading,
    error,
    mergedPages,
    refresh,
    saveOverride,
    resetOverride,
    createCustomPage,
    deleteCustomPage,
    isOverridden,
    isCustomPage: isCustomPageFromHook,
  } = useSeoPages()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editState, setEditState] = useState<EditState | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [expandedSilo, setExpandedSilo] = useState<'BNSSA' | 'MNS' | null>('BNSSA')
  const [creatingPage, setCreatingPage] = useState(false)
  const [creatingInFlight, setCreatingInFlight] = useState(false)

  // Pages mergées (pages statiques + custom + overrides) viennent du hook.
  const allBasePages = mergedPages

  const bnssaPages = mergedPages.filter((p) => p.silo === 'BNSSA')
  const mnsPages = mergedPages.filter((p) => p.silo === 'MNS')

  const filteredBnssa = search
    ? bnssaPages.filter(
        (p) =>
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          p.keyword.toLowerCase().includes(search.toLowerCase()),
      )
    : bnssaPages

  const filteredMns = search
    ? mnsPages.filter(
        (p) =>
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          p.keyword.toLowerCase().includes(search.toLowerCase()),
      )
    : mnsPages

  const selectedPage = mergedPages.find((p) => p.id === selectedId) ?? null

  // Réinitialiser le flag dirty au changement d'id
  useEffect(() => {
    setDirty(false)
  }, [selectedId])

  // Charger la page sélectionnée dans le formulaire :
  // - au changement d'id
  // - après un chargement async des pages depuis le serveur (l'override arrive)
  // - après une sauvegarde réussie (les valeurs serveur sont appliquées à mergedPages)
  // Tant que l'utilisateur n'a pas modifié (`!dirty`), on ré-aligne sur la page mérgée.
  useEffect(() => {
    if (selectedPage && !dirty) {
      setEditState(pageToEditState(selectedPage))
      console.log('[SeoEditor] editState (re)hydrated from mergedPages', {
        id: selectedPage.id,
        title: selectedPage.title?.slice(0, 60),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedId,
    selectedPage?.id,
    selectedPage?.title,
    selectedPage?.metaDescription,
    selectedPage?.h1,
    selectedPage?.intro,
    selectedPage?.heroImage,
  ])

  // Détecter les modifications
  const handleChange = useCallback(
    <K extends keyof EditState>(field: K, value: EditState[K]) => {
      setEditState((prev) => (prev ? { ...prev, [field]: value } : prev))
      setDirty(true)
    },
    [],
  )

  const handleSectionChange = useCallback(
    (idx: number, field: 'title' | 'content', value: string) => {
      setEditState((prev) => {
        if (!prev) return prev
        const sections = prev.sections.map((s, i) =>
          i === idx ? { ...s, [field]: value } : s,
        )
        return { ...prev, sections }
      })
      setDirty(true)
    },
    [],
  )

  const addSection = () => {
    setEditState((prev) =>
      prev ? { ...prev, sections: [...prev.sections, { title: '', content: '' }] } : prev,
    )
    setDirty(true)
  }

  const removeSection = (idx: number) => {
    setEditState((prev) => {
      if (!prev) return prev
      const sections = prev.sections.filter((_, i) => i !== idx)
      return { ...prev, sections }
    })
    setDirty(true)
  }

  // Sauvegarder
  const handleSave = async () => {
    if (!selectedId || !editState) return
    console.log('[SeoEditor] handleSave START', { selectedId, dirty, hasEditState: !!editState })
    setSaving(true)
    try {
      const result = await saveOverride(
        selectedId,
        {
          title: editState.title,
          metaDescription: editState.metaDescription,
          h1: editState.h1,
          intro: editState.intro,
          keyword: editState.keyword,
          sections: editState.sections,
          heroImage: editState.heroImage || '',
          isActive: editState.isActive !== false,
        },
        user?.userId ?? 'admin',
      )
      console.log('[SeoEditor] handleSave OK', { result })
      setDirty(false)
      toast.success('Page SEO sauvegardée avec succès !')
    } catch (err) {
      console.error('[SeoEditor] save failed', err)
      toast.error('Échec de la sauvegarde. Réessayez.')
    } finally {
      setSaving(false)
    }
  }

  // Réinitialiser aux valeurs d'origine
  const handleReset = async () => {
    if (!selectedId) return
    setSaving(true)
    try {
      await resetOverride(selectedId)
      // Revenir aux valeurs statiques (la page mergée n'a plus l'override)
      const refreshed = mergedPages.find((p) => p.id === selectedId)
      if (refreshed) setEditState(pageToEditState(refreshed))
      setDirty(false)
      toast('Page réinitialisée aux valeurs d\'origine.', { icon: '↩️' })
    } catch (err) {
      console.error('[SeoEditor] reset failed', err)
      toast.error('Échec de la réinitialisation.')
    } finally {
      setSaving(false)
    }
  }

  const isModified = (id: string) => isOverridden(id)
  const isCustomPage = (id: string) => isCustomPageFromHook(id)

  // Création d'une nouvelle page SEO personnalisée
  const handleCreatePage = async (newPage: SEOPage): Promise<boolean> => {
    // Vérifier l'unicité du path
    if (allBasePages.some((p) => p.path === newPage.path)) {
      toast.error('Une page existe déjà avec cette URL.')
      return false
    }
    setCreatingInFlight(true)
    try {
      await createCustomPage(newPage, user?.userId ?? 'admin')
      setCreatingPage(false)
      setSelectedId(newPage.id)
      setExpandedSilo(newPage.silo)
      toast.success('Nouvelle page SEO créée !')
      return true
    } catch (err) {
      console.error('[SeoEditor] createCustomPage failed', err)
      toast.error('Échec de la création de la page.')
      return false
    } finally {
      setCreatingInFlight(false)
    }
  }

  // Suppression d'une page custom
  const handleDeleteCustomPage = async () => {
    if (!selectedId || !isCustomPage(selectedId)) return
    if (!window.confirm('Supprimer définitivement cette page SEO personnalisée ? Cette action est irréversible.')) return
    setSaving(true)
    try {
      await deleteCustomPage(selectedId)
      setSelectedId(null)
      setEditState(null)
      setDirty(false)
      toast.success('Page SEO supprimée.')
    } catch (err) {
      console.error('[SeoEditor] delete failed', err)
      toast.error('Échec de la suppression.')
    } finally {
      setSaving(false)
    }
  }

  // Sidebar mobile drawer
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Quand l'utilisateur sélectionne une page sur mobile, fermer le drawer
  const handleSelectPage = useCallback((id: string) => {
    setSelectedId(id)
    setMobileSidebarOpen(false)
  }, [])

  return (
    <div className="flex flex-col lg:flex-row gap-0 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm min-h-[500px] lg:min-h-[600px] relative">
      {/* ── Bouton ouverture sidebar (mobile uniquement) ── */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 border border-gray-200 rounded-lg bg-white hover:bg-gray-100"
        >
          <Search className="w-4 h-4" />
          {selectedPage ? 'Changer de page' : 'Choisir une page'}
        </button>
        {selectedPage && (
          <span className="text-xs font-semibold text-gray-700 truncate ml-2 max-w-[180px]">
            {selectedPage.keyword}
          </span>
        )}
      </div>

      {/* ── Overlay drawer mobile ── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar liste des pages ── */}
      <aside
        className={`${
          mobileSidebarOpen
            ? 'fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] shadow-2xl'
            : 'hidden'
        } lg:relative lg:flex lg:w-72 lg:max-w-none lg:shadow-none lg:inset-auto lg:z-auto bg-white flex-shrink-0 border-r border-gray-200 flex flex-col`}
      >
        {/* Bouton fermeture mobile */}
        <div className="lg:hidden flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Pages SEO</span>
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Recherche */}
        <div className="p-3 border-b border-gray-200 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une page…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setCreatingPage(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <FilePlus className="w-3.5 h-3.5" />
            Nouvelle page SEO
          </button>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50"
            title="Recharger les pages SEO depuis le serveur"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            {loading ? 'Chargement…' : 'Rafraîchir'}
          </button>
          {error && (
            <p className="text-[11px] text-red-600 leading-tight">
              ⚠ {error}
            </p>
          )}
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {/* BNSSA */}
          <SiloGroup
            label="Silo BNSSA"
            color="red"
            pages={filteredBnssa}
            expanded={expandedSilo === 'BNSSA' || !!search}
            onToggle={() => setExpandedSilo((p) => (p === 'BNSSA' ? null : 'BNSSA'))}
            selectedId={selectedId}
            onSelect={handleSelectPage}
            isModified={isModified}
            isInactive={(id) => mergedPages.find((p) => p.id === id)?.isActive === false}
          />

          {/* MNS */}
          <SiloGroup
            label="Silo MNS"
            color="blue"
            pages={filteredMns}
            expanded={expandedSilo === 'MNS' || !!search}
            onToggle={() => setExpandedSilo((p) => (p === 'MNS' ? null : 'MNS'))}
            selectedId={selectedId}
            onSelect={handleSelectPage}
            isModified={isModified}
            isInactive={(id) => mergedPages.find((p) => p.id === id)?.isActive === false}
          />
        </div>

        {/* Légende */}
        <div className="p-3 border-t border-gray-100 bg-gray-50 space-y-1">
          <p className="text-[11px] text-gray-500 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
            Page modifiée (override actif)
          </p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1">
            <EyeOff className="w-3 h-3 text-gray-400" />
            Page désactivée (masquée du site public)
          </p>
        </div>
      </aside>

      {/* ── Formulaire d'édition ── */}
      <main className="flex-1 flex flex-col min-w-0 w-full">
        {!selectedPage || !editState ? (
          <div className="flex-1 flex items-center justify-center text-center p-10">
            <div>
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Search className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-1">
                Sélectionnez une page SEO
              </p>
              <p className="text-xs text-gray-500">
                Choisissez une page dans la liste de gauche pour modifier son contenu.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* En-tête éditeur */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md ${
                      selectedPage.silo === 'BNSSA'
                        ? 'bg-red-600 text-white'
                        : 'bg-blue-600 text-white'
                    }`}
                  >
                    {selectedPage.silo}
                  </span>
                  {selectedPage.isPillar && (
                    <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded-md bg-gray-200 text-gray-700">
                      Page pilier
                    </span>
                  )}
                  {isCustomPage(selectedPage.id) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-emerald-100 text-emerald-800">
                      <FilePlus className="w-3 h-3" />
                      Page personnalisée
                    </span>
                  )}
                  {isModified(selectedPage.id) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-100 text-amber-800">
                      <AlertTriangle className="w-3 h-3" />
                      Modifiée
                    </span>
                  )}
                  {dirty && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-blue-100 text-blue-800">
                      Non sauvegardé
                    </span>
                  )}
                  {!editState.isActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-md bg-gray-200 text-gray-700">
                      <EyeOff className="w-3 h-3" />
                      Désactivée
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 font-mono truncate">
                  {selectedPage.path}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                {/* Toggle on/off : visibilité de la page sur le site public */}
                <button
                  type="button"
                  onClick={() => handleChange('isActive', !editState.isActive)}
                  role="switch"
                  aria-checked={editState.isActive}
                  title={
                    editState.isActive
                      ? 'Page active : visible sur le site public. Cliquer pour désactiver.'
                      : 'Page désactivée : masquée du site public (renvoie une 404). Cliquer pour activer.'
                  }
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                    editState.isActive
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                      : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className={`relative inline-flex h-4 w-7 flex-shrink-0 rounded-full transition-colors ${
                      editState.isActive ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                        editState.isActive ? 'translate-x-3.5' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                  {editState.isActive ? (
                    <>
                      <Eye className="w-3.5 h-3.5" />
                      Active
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3.5 h-3.5" />
                      Désactivée
                    </>
                  )}
                </button>
                <a
                  href={selectedPage.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Aperçu
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
                {isModified(selectedPage.id) && !isCustomPage(selectedPage.id) && (
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Réinitialiser
                  </button>
                )}
                {isCustomPage(selectedPage.id) && (
                  <button
                    onClick={handleDeleteCustomPage}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-700 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100"
                    title="Supprimer cette page personnalisée"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Supprimer
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {saving ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
              </div>
            </div>

            {/* Formulaire */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              {/* Image hero du header */}
              <section>
                <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-gray-900 text-white text-[10px] flex items-center justify-center font-bold">
                    H
                  </span>
                  Image du header (hero)
                </h2>
                <HeroImageField
                  value={editState.heroImage}
                  onChange={(v) => handleChange('heroImage', v)}
                  silo={selectedPage.silo}
                />
              </section>

              <hr className="border-gray-100" />

              {/* Champs SEO */}
              <section>
                <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-gray-900 text-white text-[10px] flex items-center justify-center font-bold">
                    S
                  </span>
                  Métadonnées SEO
                </h2>
                <div className="space-y-4">
                  {/* Titre SEO */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Balise &lt;title&gt;
                      <MetaHint value={editState.title} min={40} max={65} />
                    </label>
                    <input
                      type="text"
                      value={editState.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Titre optimisé pour les moteurs de recherche (40-65 car.)"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Affiché dans les résultats Google. Inclure le mot-clé principal.
                    </p>
                  </div>

                  {/* Meta description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Meta description
                      <MetaHint value={editState.metaDescription} min={120} max={160} />
                    </label>
                    <textarea
                      value={editState.metaDescription}
                      onChange={(e) => handleChange('metaDescription', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Description affichée sous le titre dans Google (120-160 car.)"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Affichée dans les SERPs. Inclure le mot-clé et un appel à l'action.
                    </p>
                  </div>

                  {/* Mot-clé */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Mot-clé principal
                    </label>
                    <input
                      type="text"
                      value={editState.keyword}
                      onChange={(e) => handleChange('keyword', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex : emploi bnssa"
                    />
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* Contenu de la page */}
              <section>
                <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-gray-900 text-white text-[10px] flex items-center justify-center font-bold">
                    C
                  </span>
                  Contenu de la page
                </h2>
                <div className="space-y-4">
                  {/* H1 */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Titre H1
                      <MetaHint value={editState.h1} min={30} max={70} />
                    </label>
                    <input
                      type="text"
                      value={editState.h1}
                      onChange={(e) => handleChange('h1', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Titre principal visible sur la page (30-70 car.)"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Titre H1 affiché en haut de la page. Doit contenir le mot-clé.
                    </p>
                  </div>

                  {/* Intro */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Paragraphe d'introduction
                      <span className="text-[11px] text-gray-400 ml-1 font-normal">
                        ({editState.intro.length} car.)
                      </span>
                    </label>
                    <RichTextEditor
                      value={editState.intro}
                      onChange={(html) => handleChange('intro', html)}
                      placeholder="Paragraphe introductif de la page, visible juste après le H1..."
                      minHeight={140}
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Mise en forme enrichie : gras, italique, liens, listes…
                    </p>
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* Sections */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-gray-900 text-white text-[10px] flex items-center justify-center font-bold">
                      §
                    </span>
                    Sections de contenu ({editState.sections.length})
                  </h2>
                  <button
                    onClick={addSection}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Ajouter une section
                  </button>
                </div>

                <div className="space-y-4">
                  {editState.sections.map((section, idx) => (
                    <SectionCard
                      key={idx}
                      idx={idx}
                      section={section}
                      onChange={handleSectionChange}
                      onRemove={() => removeSection(idx)}
                      canRemove={editState.sections.length > 1}
                    />
                  ))}
                </div>
              </section>

              {/* Preview SEO */}
              <section className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <h2 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">
                  Aperçu Google (simulation)
                </h2>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm max-w-xl">
                  <p className="text-[13px] text-blue-700 font-medium leading-snug truncate">
                    {editState.title || 'Titre de la page'}
                  </p>
                  <p className="text-[11px] text-emerald-700 mt-0.5 truncate">
                    https://bnssa-mns.fr{selectedPage.path}
                  </p>
                  <p className="text-[12px] text-gray-600 mt-1 leading-relaxed line-clamp-2">
                    {editState.metaDescription || 'Description de la page…'}
                  </p>
                </div>
              </section>

              {/* Bouton sauvegarde bas de page */}
              <div className="flex items-center justify-end gap-3 pt-2 pb-4">
                {isModified(selectedPage.id) && (
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Réinitialiser
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Sauvegarde…' : 'Sauvegarder les modifications'}
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* ── Modale de création d'une nouvelle page SEO ── */}
      {creatingPage && (
        <CreatePageModal
          existingPaths={allBasePages.map((p) => p.path)}
          existingPages={allBasePages}
          onClose={() => setCreatingPage(false)}
          onCreate={handleCreatePage}
          inFlight={creatingInFlight}
        />
      )}
    </div>
  )
}

// ─── SiloGroup ────────────────────────────────────────────────────────────────
function SiloGroup({
  label,
  color,
  pages,
  expanded,
  onToggle,
  selectedId,
  onSelect,
  isModified,
  isInactive,
}: {
  label: string
  color: 'red' | 'blue'
  pages: SEOPage[]
  expanded: boolean
  onToggle: () => void
  selectedId: string | null
  onSelect: (id: string) => void
  isModified: (id: string) => boolean
  isInactive: (id: string) => boolean
}) {
  const accent = color === 'red' ? 'text-red-600' : 'text-blue-600'
  const dot = color === 'red' ? 'bg-red-500' : 'bg-blue-500'

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-left"
      >
        <span className={`text-xs font-bold uppercase tracking-wider ${accent}`}>
          {label}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">{pages.length} pages</span>
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <ul className="pb-1">
          {pages.map((p) => {
            const active = selectedId === p.id
            const modified = isModified(p.id)
            const inactive = isInactive(p.id)
            return (
              <li key={p.id}>
                <button
                  onClick={() => onSelect(p.id)}
                  className={`w-full text-left flex items-center gap-2 px-4 py-2 text-xs transition-colors ${
                    active
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  } ${p.isPillar ? 'font-bold' : 'pl-7'} ${
                    inactive && !active ? 'opacity-50' : ''
                  }`}
                >
                  {p.isPillar && (
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-white' : dot}`} />
                  )}
                  <span
                    className={`truncate flex-1 ${
                      inactive ? 'line-through decoration-1' : ''
                    }`}
                  >
                    {p.isPillar ? p.keyword.toUpperCase() : p.keyword}
                  </span>
                  {inactive && (
                    <EyeOff
                      className={`w-3 h-3 flex-shrink-0 ${active ? 'text-white/70' : 'text-gray-400'}`}
                      aria-label="Page désactivée"
                    />
                  )}
                  {modified && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? 'bg-amber-300' : 'bg-amber-400'}`}
                      title="Page modifiée"
                    />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ─── SectionCard ─────────────────────────────────────────────────────────────
function SectionCard({
  idx,
  section,
  onChange,
  onRemove,
  canRemove,
}: {
  idx: number
  section: Section
  onChange: (idx: number, field: 'title' | 'content', value: string) => void
  onRemove: () => void
  canRemove: boolean
}) {
  const [collapsed, setCollapsed] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingImg, setUploadingImg] = useState(false)

  // Append HTML snippet at the end of the current section content (RichTextEditor sync via prop)
  const appendHtml = (snippet: string) => {
    const current = section.content || ''
    onChange(idx, 'content', current + snippet)
  }

  const insertImageFromUrl = () => {
    const url = window.prompt('URL de l\'image (https://…) :', 'https://')
    if (!url) return
    const alt = window.prompt('Texte alternatif (alt) :', '') ?? ''
    appendHtml(`\n<img src="${url}" alt="${alt.replace(/"/g, '&quot;')}" />\n`)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 Mo).')
      return
    }
    setUploadingImg(true)
    try {
      const url = await uploadOneFile(file)
      const alt = file.name.replace(/\.[^.]+$/, '')
      appendHtml(`\n<img src="${url}" alt="${alt}" />\n`)
      toast.success('Image insérée')
    } catch (err) {
      console.error('[SeoEditor] image upload failed', err)
      toast.error('Échec de l\'envoi de l\'image')
    } finally {
      setUploadingImg(false)
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* En-tête section */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          )}
          <span className="text-xs font-semibold text-gray-600 mr-1">§{idx + 1}</span>
          <span className="text-xs text-gray-800 truncate">
            {section.title || <span className="text-gray-400 italic">Section sans titre</span>}
          </span>
        </button>
        {canRemove && (
          <button
            onClick={onRemove}
            className="ml-2 p-1 rounded hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors"
            title="Supprimer cette section"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Corps section */}
      {!collapsed && (
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Titre de la section (H2/H3)
            </label>
            <input
              type="text"
              value={section.title}
              onChange={(e) => onChange(idx, 'title', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex : Comment devenir BNSSA ?"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <label className="block text-[11px] font-semibold text-gray-600">
                Contenu
                <span className="text-gray-400 font-normal ml-1">({section.content.length} car.)</span>
              </label>
              {/* Toolbar : upload image / image via URL (le RichTextEditor gère déjà gras/italique/liens/listes) */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImg}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200 rounded-md hover:bg-gray-100 disabled:opacity-50"
                  title="Importer une image"
                >
                  <Upload className="w-3 h-3" />
                  {uploadingImg ? 'Envoi…' : 'Image'}
                </button>
                <button
                  type="button"
                  onClick={insertImageFromUrl}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200 rounded-md hover:bg-gray-100"
                  title="Insérer une image via URL"
                >
                  <ImageIcon className="w-3 h-3" />
                  URL img
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>
            <RichTextEditor
              value={section.content}
              onChange={(html) => onChange(idx, 'content', html)}
              placeholder="Rédigez le contenu de la section : paragraphes, gras, italique, liens, listes…"
              minHeight={220}
            />
            <p className="mt-1 text-[11px] text-gray-400 leading-relaxed">
              Mise en forme enrichie : gras, italique, titres, listes, liens. Utilisez les boutons ci-dessus pour insérer une image (upload ou URL).
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── HeroImageField ──────────────────────────────────────────────────────────
function HeroImageField({
  value,
  onChange,
  silo,
}: {
  value: string
  onChange: (v: string) => void
  silo: 'BNSSA' | 'MNS'
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10 Mo).')
      return
    }
    setUploading(true)
    try {
      const url = await uploadOneFile(file)
      onChange(url)
      toast.success('Image hero mise à jour')
    } catch (err) {
      console.error('[SeoEditor] hero upload failed', err)
      toast.error('Échec de l\'envoi de l\'image')
    } finally {
      setUploading(false)
    }
  }

  const accent = silo === 'BNSSA' ? 'from-red-900/60' : 'from-blue-900/60'

  return (
    <div className="space-y-3">
      {/* Aperçu */}
      <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100 aspect-[16/6]">
        {value ? (
          <>
            <img
              src={value}
              alt="Aperçu image hero"
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-r ${accent} via-black/30 to-transparent`} />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-white/90 hover:bg-white text-gray-700 rounded-md shadow-sm"
              title="Retirer l'image personnalisée (revenir à l'image par défaut)"
            >
              <X className="w-3 h-3" />
              Retirer
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-1">
            <ImageIcon className="w-7 h-7" />
            <p className="text-xs">Image par défaut du silo {silo} utilisée</p>
          </div>
        )}
      </div>

      {/* Contrôles */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… (URL d'une image)"
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? 'Envoi…' : 'Importer une image'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      <p className="text-[11px] text-gray-400">
        Si aucune image n'est définie, l'image par défaut du silo ({silo === 'BNSSA' ? 'rouge BNSSA' : 'bleu MNS'}) sera utilisée. Format recommandé : 1920×1080 ou plus large.
      </p>
    </div>
  )
}

// ─── CreatePageModal ─────────────────────────────────────────────────────────
function CreatePageModal({
  existingPaths,
  existingPages,
  onClose,
  onCreate,
  inFlight,
}: {
  existingPaths: string[]
  existingPages: SEOPage[]
  onClose: () => void
  onCreate: (page: SEOPage) => boolean | Promise<boolean>
  inFlight?: boolean
}) {
  const [silo, setSilo] = useState<SiloType>('BNSSA')
  const [parentPath, setParentPath] = useState<string>('/bnssa')
  const [slug, setSlug] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [metaDescription, setMetaDescription] = useState<string>('')
  const [h1, setH1] = useState<string>('')
  const [intro, setIntro] = useState<string>('')

  // Pages mères possibles : tous les piliers + pages existantes du silo sélectionné
  const possibleParents = existingPages.filter((p) => p.silo === silo)

  // Quand on change de silo, recaler la page mère par défaut sur le pilier du silo
  useEffect(() => {
    const pillar = existingPages.find((p) => p.silo === silo && p.isPillar)
    if (pillar) setParentPath(pillar.path)
  }, [silo, existingPages])

  // Auto-suggestion du slug à partir du mot-clé tant que l'utilisateur n'a pas saisi de slug manuel
  const [slugTouched, setSlugTouched] = useState(false)
  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugify(keyword))
    }
  }, [keyword, slugTouched])

  const computedPath =
    parentPath && slug ? `${parentPath.replace(/\/+$/, '')}/${slug}` : ''

  const pathExists = computedPath && existingPaths.includes(computedPath)

  const canSubmit =
    silo &&
    parentPath &&
    slug &&
    keyword.trim() &&
    title.trim() &&
    metaDescription.trim() &&
    h1.trim() &&
    !pathExists

  const handleSubmit = () => {
    if (!canSubmit) return
    const page: SEOPage = {
      id: `custom-${silo.toLowerCase()}-${slug}-${Date.now()}`,
      path: computedPath,
      slug,
      parent: parentPath,
      isPillar: false,
      silo,
      keyword: keyword.trim(),
      title: title.trim(),
      metaDescription: metaDescription.trim(),
      h1: h1.trim(),
      intro: intro.trim(),
      sections: [
        {
          title: 'Introduction',
          content: `<p>Rédigez ici le contenu de votre nouvelle page sur le mot-clé « ${keyword.trim()} ».</p>`,
        },
      ],
      color: silo === 'BNSSA' ? 'red' : 'blue',
    }
    onCreate(page)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-2 sm:my-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <FilePlus className="w-4 h-4 text-emerald-600" />
              Créer une nouvelle page SEO
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Configurez le silo, la page mère (breadcrumb), l'URL et les métadonnées principales.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-5 max-h-[75vh] sm:max-h-[70vh] overflow-y-auto">
          {/* Silo */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Silo
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSilo('BNSSA')}
                className={`px-4 py-2.5 text-sm font-bold rounded-lg border-2 transition-colors ${
                  silo === 'BNSSA'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                }`}
              >
                Silo BNSSA
              </button>
              <button
                type="button"
                onClick={() => setSilo('MNS')}
                className={`px-4 py-2.5 text-sm font-bold rounded-lg border-2 transition-colors ${
                  silo === 'MNS'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                }`}
              >
                Silo MNS
              </button>
            </div>
          </div>

          {/* Page mère */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Page mère (utilisée pour le fil d'Ariane / breadcrumb)
            </label>
            <select
              value={parentPath}
              onChange={(e) => setParentPath(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {possibleParents.map((p) => (
                <option key={p.id} value={p.path}>
                  {p.isPillar ? `★ ${p.title} (pilier)` : `↳ ${p.title}`}{' '}
                  — {p.path}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              La nouvelle page sera publiée comme enfant de cette page. L'URL finale en découle automatiquement.
            </p>
          </div>

          {/* Mot-clé */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Mot-clé principal
            </label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Ex : recyclage bnssa, formation mns paris…"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Slug + URL preview */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Slug (segment d'URL)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
                {parentPath || '/…'}/
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(slugify(e.target.value))
                  setSlugTouched(true)
                }}
                placeholder="recyclage-bnssa"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>
            {computedPath && (
              <p className={`mt-1 text-[11px] font-mono ${pathExists ? 'text-red-600' : 'text-emerald-600'}`}>
                {pathExists
                  ? `⚠ Une page existe déjà à ${computedPath}`
                  : `URL finale : https://bnssa-mns.fr${computedPath}`}
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Balise &lt;title&gt; (Meta Title)
              <MetaHint value={title} min={40} max={65} />
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre optimisé affiché dans Google (40-65 car.)"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Meta description */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Meta description
              <MetaHint value={metaDescription} min={120} max={160} />
            </label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows={3}
              placeholder="Description affichée sous le titre dans Google (120-160 car.)"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* H1 */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Titre H1
              <MetaHint value={h1} min={30} max={70} />
            </label>
            <input
              type="text"
              value={h1}
              onChange={(e) => setH1(e.target.value)}
              placeholder="Titre principal affiché en haut de la page (30-70 car.)"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Intro */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Paragraphe d'introduction
              <span className="text-[11px] text-gray-400 ml-1 font-normal">
                ({intro.length} car.)
              </span>
            </label>
            <RichTextEditor
              value={intro}
              onChange={(html) => setIntro(html)}
              placeholder="Texte d'introduction visible juste sous le H1…"
              minHeight={140}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Mise en forme enrichie disponible. Une première section vide sera créée automatiquement.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={inFlight}
            className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-200 bg-white rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || !!inFlight}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {inFlight ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus className="w-4 h-4" />}
            {inFlight ? 'Création…' : 'Créer la page'}
          </button>
        </div>
      </div>
    </div>
  )
}
